import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { RefreshCw } from 'lucide-react';
import { apiGet } from '../lib/api.js';
import { useAuth } from '../store/auth.js';
import { getSocket } from '../lib/socket.js';
import { SYNC_SOURCES } from '../lib/syncHistory.js';
import ResyncPanel from '../components/sync/ResyncPanel.jsx';
import SyncSourceTabs from '../components/sync/SyncSourceTabs.jsx';
import MasterSyncPanel from '../components/sync/MasterSyncPanel.jsx';
import ManagerSyncPanel from '../components/sync/ManagerSyncPanel.jsx';
import SyncChecklist from '../components/sync/SyncChecklist.jsx';
import SyncHistoryTable from '../components/sync/SyncHistoryTable.jsx';
import ManagerPendingApprovals from '../components/sync/ManagerPendingApprovals.jsx';
import ProducerPageNextStep from '../components/producerPage/ProducerPageNextStep.jsx';

export default function SyncAccount() {
  const { user, agent } = useAuth();
  const qc = useQueryClient();
  const isManager = agent?.role === 'admin' || agent?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'superadmin';

  // Real-time listener for sync request completion / rejections
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;

    const refresh = () => {
      qc.invalidateQueries({ queryKey: ['sync-status'] });
      qc.invalidateQueries({ queryKey: ['sync-pending'] });
      qc.invalidateQueries({ queryKey: ['sync-history'] });
    };
    function handleCompleted(data) {
      // Master clones already toast from the button; only announce manager approvals.
      if (data?.sourceType !== 'master') toast.success('🎉 Manager approved! Your CRM templates, drips, and AI bots are now loaded.');
      refresh();
    }
    function handleRejected() {
      toast('Sync request was declined by manager.', { icon: 'ℹ️' });
      refresh();
    }
    function handleRequested(data) {
      if (isManager) {
        toast(`🔔 New sync request from ${data?.requester?.name || 'an agent'}!`, { duration: 5000 });
        qc.invalidateQueries({ queryKey: ['sync-pending'] });
      }
    }

    socket.on('sync:completed', handleCompleted);
    socket.on('sync:rejected', handleRejected);
    socket.on('sync:requested', handleRequested);
    return () => {
      socket.off('sync:completed', handleCompleted);
      socket.off('sync:rejected', handleRejected);
      socket.off('sync:requested', handleRequested);
    };
  }, [qc, isManager]);

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto pb-12">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-civix-purpleSoft/90 uppercase tracking-[0.3em] text-xs font-semibold">Growth ➔ Operational Sync</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-3">
            <RefreshCw className="w-7 h-7 text-civix-cyan" /> Sync Account Settings
          </h1>
          <p className="text-white/60 mt-1.5 max-w-2xl text-sm leading-relaxed">
            Skip weeks of trial and error. Clone a proven CIVIX master setup instantly, or inherit your team manager's operational setup — including AI text bots, multi-step SMS drips, email sequences, pipeline stages and tags.
          </p>
        </div>
      </header>

      {/* Main Grid: Requester Pane + (Manager Approval Hub if manager/superadmin) */}
      <div className={`grid gap-6 ${isManager ? 'xl:grid-cols-12' : ''}`}>
        <div className={isManager ? 'xl:col-span-7 space-y-6' : 'max-w-5xl mx-auto space-y-6 w-full'}>
          <AgentSyncCard />
          <SyncHistoryTable />
        </div>

        {isManager && (
          <div className="xl:col-span-5 space-y-6">
            <ManagerPendingApprovals />
          </div>
        )}
      </div>

      {/* Optional last onboarding step */}
      <ProducerPageNextStep />
    </div>
  );
}

/** Dual-source sync card: pick a source, see what gets cloned, run the sync. */
function AgentSyncCard() {
  const [source, setSource] = useState(SYNC_SOURCES.MASTER);

  const { data: mgrData, isLoading: isLoadingManagers } = useQuery({
    queryKey: ['sync-managers'],
    queryFn: () => apiGet('/sync/managers'),
    staleTime: 30000,
  });
  const { data: statusData } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => apiGet('/sync/status'),
    staleTime: 10000,
    refetchInterval: 10000,
  });

  const managers = mgrData?.managers || [];
  const latestRequest = statusData?.latestRequest || null;
  const isPending = latestRequest?.status === 'pending' && (latestRequest?.sourceType || 'manager') === 'manager';
  const isApproved = statusData?.isSynced || latestRequest?.status === 'approved';

  return (
    <div className="panel p-6 space-y-6 border border-civix-purple/30 bg-[#0d0714] shadow-2xl rounded-2xl relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-civix-purple/10 blur-3xl pointer-events-none" />

      {isApproved && (
        <ResyncPanel
          status={{
            ...statusData,
            syncedManagerName: statusData?.syncedManagerName || latestRequest?.targetManagerId?.name,
          }}
          managers={managers}
        />
      )}

      <SyncSourceTabs value={source} onChange={setSource} />

      {source === SYNC_SOURCES.MASTER ? (
        <MasterSyncPanel />
      ) : (
        <ManagerSyncPanel
          managers={managers}
          isLoadingManagers={isLoadingManagers}
          latestRequest={latestRequest}
          isPending={isPending}
          isApproved={isApproved}
        />
      )}

      <SyncChecklist />
    </div>
  );
}
